<template>
    <b-modal :id="id" size="lg" title="Prior Authorization" @show="onShow" @hide="$emit('hide')" @hidden="onModalHidden"
        scrollable>
        <b-container fluid>

            <b-row v-if="lastDocumentDate != null">
                <b-col>
                    <b-alert show variant="info">
                        Last document uploaded for this patient was at {{ lastDocumentDate | formatDate('lll') }}.
                        <b>
                            <a style="color:white" @click.prevent v-b-modal.patient_documents_modal>
                                See Documents...
                            </a>
                        </b>
                    </b-alert>
                </b-col>
            </b-row>

            <b-row v-if="isNonScript" class="mb-1">
                <b-col>
                    <b-badge variant="info">External</b-badge>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <b>Patient: </b> <span>{{ patientName }}</span>
                </b-col>
                <b-col style="gap: 10px;" class="d-flex justify-content-end">
                    <router-link v-if="value?.patient?.id" target="_blank"
                        :to="{ name: 'PatientDashboard', params: { id: value.patient.id } }">
                        <span>Go To Profile</span> <i class="fas fa-user"></i>
                    </router-link>
                    <router-link v-if="value?.prescription" target="_blank" :to="prescriptionDetailsRoute">
                        <span>Go To Rx</span> <i class="fas fa-file-prescription"></i>
                    </router-link>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="4">
                    <strong>DOB: </strong>{{ patientDateOfBirth || '—' }}
                </b-col>
                <b-col cols="4">
                    <strong>Sex: </strong>{{ value?.patient?.sex || '—' }}
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="6">
                    <strong>Phone: </strong>{{ value?.patient?.phoneDisplay || '—' }}
                </b-col>
                <b-col cols="6">
                    <strong>Cell: </strong>{{ value?.patient?.cellDisplay || '—' }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Address: </strong>{{ value?.patient?.fullAddress || '—' }}
                </b-col>
            </b-row>
            <template v-if="hasSelectedPlan">
                <b-row>
                    <b-col>
                        <strong>Plan: </strong>{{ selectedPatientPlan.planName || '—' }}
                        <span class="ml-2"><strong>BIN: </strong>{{ selectedPatientPlan.planBin || '—' }}</span>
                        <span class="ml-2"><strong>PCN: </strong>{{ selectedPatientPlan.planPcn || '—' }}</span>
                    </b-col>
                </b-row>
                <b-row class="mb-1">
                    <b-col>
                        <strong>Card: </strong>{{ selectedPatientPlan.cardholderId || '—' }}
                        <span class="ml-2"><strong>Group: </strong>{{ selectedPatientPlan.groupId || '—' }}</span>
                        <span class="ml-2"><strong>Person: </strong>{{ selectedPatientPlan.personCode || '—' }}</span>
                    </b-col>
                </b-row>
            </template>
            <b-row v-else>
                <b-col>
                    <strong>Plan: </strong>
                    <span class="text-muted">No plan selected — choose one under Patient Plan below</span>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Drug: </strong>{{ drugDisplayName }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Prescriber: </strong>
                    <span v-if="prescriberDisplayName">{{ prescriberDisplayName }}</span>
                    <span v-else class="text-muted">Not linked</span>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Program: </strong>{{ value?.programName || '-' }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Initiated: </strong>{{ createDate }}
                </b-col>
                <b-col>
                    <strong>Last Updated: </strong>{{ updateDate }}
                </b-col>
            </b-row>
            <b-row v-if="value?.prescription">
                <b-col>
                    <strong>Rx Id: </strong>{{ value.prescription.storeID }}-{{ value.prescription.rxNumber }}-{{
                        value.prescription.rfNumber
                    }}
                </b-col>
            </b-row>
            <template v-if="isNonScript">
                <hr>
                <b-row>
                    <b-col>
                        <strong>Link Drug: </strong>
                        <SearchComponent v-model="selectedDrug" search-u-r-l="/Drug" :hide-label="true"
                            class="mt-1" clearable />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <strong>Link Prescriber: </strong>
                        <SearchComponent v-model="selectedPrescriber" search-u-r-l="/Prescriber" :hide-label="true"
                            class="mt-1" clearable />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <strong>Program: </strong>
                        <SearchComponent v-model="selectedProgram" search-u-r-l="/Program" :hide-label="true"
                            class="mt-1" clearable prepopulate @input="onProgramInput" />
                    </b-col>
                </b-row>
            </template>

            <div class="mt-2 mb-2">
                <a href="#" @click.prevent="toggleRxDetails">
                    <i :class="{ 'fa fa-fw fa-chevron-right': true, 'fa-rotate-90': showRxDetails }"
                        aria-hidden="true" style="transition: all .2s;" />
                    <strong>Prescription Details</strong>
                </a>

                <div v-show="showRxDetails" class="mt-2 ml-3">
                    <div v-if="rxDetailsError" class="text-danger">
                        Failed to load prescription details.
                        <a href="#" @click.prevent="loadFullPrescription">Retry</a>
                    </div>
                    <div v-else-if="!fullPrescription" class="text-muted">
                        <b-spinner small /> Loading prescription details...
                    </div>
                    <template v-else>
                        <b-row>
                            <b-col>
                                <strong>Directions/SIG: </strong>{{ fullPrescription.directions }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <strong>Quantity: </strong>{{ fullPrescription.quantity }}
                            </b-col>
                            <b-col>
                                <strong>Days Supply: </strong>{{ fullPrescription.daySupply }}
                            </b-col>
                            <b-col>
                                <strong>Refills: </strong>{{ fullPrescription.refills }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <strong>NDC: </strong>{{ fullPrescription.ndc11 || 'N/A' }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <strong>Written Date: </strong>{{ formatDate(fullPrescription.writtenDate) }}
                            </b-col>
                            <b-col>
                                <strong>Fill Date: </strong>{{ formatDate(fullPrescription.fillDate) }}
                            </b-col>
                            <b-col>
                                <strong>Expiration Date: </strong>{{ formatDate(fullPrescription.expirationDate) }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <strong>Rx Status: </strong>{{ rxStatus }}
                            </b-col>
                        </b-row>
                        <b-row v-if="fullPrescription.memo">
                            <b-col>
                                <strong>Memo: </strong>{{ fullPrescription.memo }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <strong>Program: </strong>{{ fullPrescription.programName || 'N/A' }}
                            </b-col>
                            <b-col>
                                <strong>Store: </strong>{{ fullPrescription.storeName || 'N/A' }}
                            </b-col>
                        </b-row>
                        <b-row v-if="fullPrescription.totalPrice != null">
                            <b-col>
                                <strong>Total: </strong>${{ fullPrescription.totalPrice?.toFixed(2) }}
                            </b-col>
                            <b-col>
                                <strong>Cost: </strong>{{ fullPrescription.cost != null ? '$' + fullPrescription.cost.toFixed(2) : 'N/A' }}
                            </b-col>
                            <b-col>
                                <strong>Patient Pay: </strong>{{ fullPrescription.patientPay != null ? '$' + fullPrescription.patientPay.toFixed(2) : 'N/A' }}
                            </b-col>
                            <b-col>
                                <strong>Tax: </strong>${{ fullPrescription.tax?.toFixed(2) || '0.00' }}
                            </b-col>
                        </b-row>

                        <div class="mt-2">
                            <strong>Latest Claim:</strong>
                            <div v-if="latestClaim" class="ml-2 mt-1">
                                <b-row>
                                    <b-col>
                                        <strong>Status: </strong>{{ latestClaim.transactionStatus }}
                                    </b-col>
                                    <b-col>
                                        <strong>Plan: </strong>{{ latestClaim.planName }}
                                    </b-col>
                                </b-row>
                                <b-row v-if="latestClaim.rejectCodeList && latestClaim.rejectCodeList.length">
                                    <b-col>
                                        <strong>Reject Codes: </strong>
                                        <span class="text-danger">{{ latestClaim.rejectCodeList.join(', ') }}</span>
                                    </b-col>
                                </b-row>
                                <b-row v-if="latestClaim.responseMessage">
                                    <b-col>
                                        <strong>Response: </strong>{{ latestClaim.responseMessage }}
                                    </b-col>
                                </b-row>
                            </div>
                            <div v-else class="ml-2 mt-1 text-muted">No claims found.</div>
                        </div>
                    </template>
                </div>
            </div>

            <hr>
            <b-row class="mb-2">
                <b-col>
                    <strong>Status:</strong>
                    <EnumSelector :value="value.status" :enum-codes="PriorAuthorizationStatusCode"
                        :disabled="!canEditPA" class="mb-0" @input="onStatusSelected" />
                </b-col>
            </b-row>
            <b-row v-if="value.completeReason || requiresReason" class="mb-2">
                <b-col>
                    <strong>Reason{{ requiresReason ? '*' : '' }}:</strong>
                    <b-input
                        v-if="!completeReasonOptions.length || customReason || (value.completeReason && !completeReasonOptions.includes(value.completeReason))"
                        v-model="value.completeReason" />
                    <template v-else>
                        <b-form-select v-model="value.completeReason" :options="completeReasonOptions" />
                        <a v-if="canEnterCustomReason" href="#" @click.prevent="customReason = true">Custom
                            Reason...</a>
                    </template>
                </b-col>
            </b-row>
            <b-row class="mb-2">
                <b-col>
                    <strong>Approved Date:</strong>
                    <el-date-picker v-model="value.approvalDate" format="MM/dd/yyyy" type="date" today-button
                        reset-button />
                </b-col>
                <b-col>
                    <strong>Expiration Date:</strong>
                    <el-date-picker v-model="value.expirationDate" format="MM/dd/yyyy" type="date" today-button
                        reset-button />
                </b-col>
            </b-row>
            <b-row class="mb-2">
                <b-col>
                    <strong>Case Number:</strong>
                    <b-input v-model="value.caseNumber" maxlength="50" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Patient Plan:</strong>
                    <SearchComponent :id="selectedPatientPlan?.id" v-model="selectedPatientPlan"
                        :search-u-r-l="`/Insurance/GetPatientPlans/` + value.patient?.id" :hide-label="true"
                        class="mt-2" clearable prepopulate />
                </b-col>
                <b-col>
                    <strong>Type:</strong>
                    <EnumSelector v-model="value.type" :enum-codes="PriorAuthorizationType" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-check v-model="value.clinicalNoteTranscription">Clinical Note Transcription</b-check>
                </b-col>
            </b-row>
            <hr />
            <b-row v-if="value.coverMyMedsId || value.latestCallback">
                <b-col>
                    <strong>CMM Id: </strong>{{ value.coverMyMedsId }}
                </b-col>
                <b-col class="text-right" v-if="value.coverMyMedsId">
                    <a href="#" type="button" @click.prevent="coverMyMedsClick">
                        View Cover My Meds Profile
                    </a>
                </b-col>
            </b-row>
            <b-row v-if="value.latestCallback">
                <b-col>
                    <strong>CMM Event: </strong>
                    {{ value.latestCallback.event_key }} - {{ value.latestCallback.message }}
                </b-col>
            </b-row>
            <!-- Ingested Data (sidecar) for non-script PAs -->
            <template v-if="isNonScript && parsedSidecar">
                <hr />
                <b-row>
                    <b-col>
                        <a href="#" @click.prevent="showSidecar = !showSidecar">
                            <i :class="{ 'fa fa-fw fa-chevron-right': true, 'fa-rotate-90': showSidecar }"
                                aria-hidden="true" style="transition: all .2s;" />
                            <strong>Ingested Data</strong>
                        </a>
                    </b-col>
                </b-row>
                <div v-if="showSidecar" class="mt-2">
                    <!-- Reject Codes & Messages -->
                    <div v-if="sidecarRejectInfo.length" class="mb-3">
                        <strong class="text-danger">Rejection Information</strong>
                        <b-table :items="sidecarRejectInfo" :fields="['code', 'message']" small striped
                            class="mt-1 mb-0" />
                    </div>

                    <!-- Drug / Prescription Info -->
                    <div class="mb-3">
                        <strong>Prescription Details</strong>
                        <b-row class="mt-1 small">
                            <b-col cols="6" v-if="parsedSidecar.ProductName">
                                <strong>Drug: </strong>{{ parsedSidecar.ProductName }}
                                <span v-if="parsedSidecar.ProductStrength"> {{ parsedSidecar.ProductStrength }}</span>
                                <span v-if="parsedSidecar.ProductForm"> {{ parsedSidecar.ProductForm }}</span>
                            </b-col>
                            <b-col cols="6" v-if="parsedSidecar.NDC">
                                <strong>NDC: </strong>{{ parsedSidecar.NDC }}
                            </b-col>
                        </b-row>
                        <b-row class="small">
                            <b-col cols="6" v-if="parsedSidecar.DosingInstruction">
                                <strong>Sig: </strong>{{ parsedSidecar.DosingInstruction }}
                            </b-col>
                            <b-col cols="3" v-if="parsedSidecar.QuantityPrescribed">
                                <strong>Qty: </strong>{{ parsedSidecar.QuantityPrescribed }}
                            </b-col>
                            <b-col cols="3" v-if="parsedSidecar.DaysSupply">
                                <strong>Days: </strong>{{ parsedSidecar.DaysSupply }}
                            </b-col>
                        </b-row>
                        <b-row class="small">
                            <b-col cols="3" v-if="parsedSidecar.DateWritten">
                                <strong>Date Written: </strong>{{ parsedSidecar.DateWritten | formatDate('L') }}
                            </b-col>
                            <b-col cols="3" v-if="parsedSidecar.OrderCreateDate">
                                <strong>Order Created: </strong>{{ parsedSidecar.OrderCreateDate | formatDate('L') }}
                            </b-col>
                            <b-col cols="2" v-if="parsedSidecar.NewOrRefill">
                                <strong>New/Refill: </strong>{{ parsedSidecar.NewOrRefill }}
                            </b-col>
                            <b-col cols="2" v-if="parsedSidecar.OriginFillNumber != null">
                                <strong>Fill #: </strong>{{ parsedSidecar.OriginFillNumber }}
                            </b-col>
                            <b-col cols="2" v-if="parsedSidecar.TotalFillNumber != null">
                                <strong>Total Fills: </strong>{{ parsedSidecar.TotalFillNumber }}
                            </b-col>
                        </b-row>
                        <b-row class="small" v-if="parsedSidecar.RefillRemaining != null">
                            <b-col cols="3">
                                <strong>Refills Remaining: </strong>{{ parsedSidecar.RefillRemaining }}
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Prescriber Info (from sidecar) -->
                    <div v-if="parsedSidecar.PrescriberLastName" class="mb-3">
                        <strong>Prescriber (Source Data)</strong>
                        <b-row class="mt-1 small">
                            <b-col cols="6">
                                <strong>Name: </strong>{{ parsedSidecar.PrescriberFirstName }} {{ parsedSidecar.PrescriberLastName }}
                            </b-col>
                            <b-col cols="6" v-if="parsedSidecar.PrescriberNPI">
                                <strong>NPI: </strong>{{ parsedSidecar.PrescriberNPI }}
                            </b-col>
                        </b-row>
                        <b-row class="small">
                            <b-col cols="6" v-if="parsedSidecar.PrescriberPhone">
                                <strong>Phone: </strong>{{ parsedSidecar.PrescriberPhone }}
                            </b-col>
                            <b-col cols="6" v-if="parsedSidecar.PrescriberFax">
                                <strong>Fax: </strong>{{ parsedSidecar.PrescriberFax }}
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Origin Pharmacy -->
                    <div v-if="parsedSidecar.OriginPharmacyName" class="mb-3">
                        <strong>Dispensing Store</strong>
                        <b-row class="mt-1 small">
                            <b-col cols="6">
                                <strong>Name: </strong>{{ parsedSidecar.OriginPharmacyName }}
                            </b-col>
                            <b-col cols="3" v-if="parsedSidecar.OriginStoreNumber">
                                <strong>Store #: </strong>{{ parsedSidecar.OriginStoreNumber }}
                            </b-col>
                            <b-col cols="3" v-if="parsedSidecar.OriginRxNumber">
                                <strong>Rx #: </strong>{{ parsedSidecar.OriginRxNumber }}
                            </b-col>
                        </b-row>
                        <b-row class="small" v-if="parsedSidecar.OriginPharmacyNPI">
                            <b-col cols="6">
                                <strong>NPI: </strong>{{ parsedSidecar.OriginPharmacyNPI }}
                            </b-col>
                            <b-col cols="6" v-if="parsedSidecar.OriginPharmacyAddress">
                                <strong>Address: </strong>{{ parsedSidecar.OriginPharmacyAddress }},
                                {{ parsedSidecar.OriginPharmacyCity }} {{ parsedSidecar.OriginPharmacyState }} {{ parsedSidecar.OriginPharmacyZip }}
                            </b-col>
                        </b-row>
                        <div v-if="isLoadingDispensing" class="small text-muted mt-1">
                            Fetching details...
                        </div>
                        <b-row class="small" v-if="dispensingPhoneNumber || dispensingFaxNumber">
                            <b-col cols="6" v-if="dispensingPhoneNumber">
                                <strong>Phone: </strong>{{ dispensingPhoneNumber }}
                            </b-col>
                            <b-col cols="6" v-if="dispensingFaxNumber">
                                <strong>Fax: </strong>{{ dispensingFaxNumber }}
                            </b-col>
                        </b-row>
                        <b-row class="small" v-if="directoryHours">
                            <b-col>
                                <strong>Hours: </strong>{{ directoryHours }}
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Insurance Info -->
                    <div v-if="parsedSidecar.PrimaryBIN" class="mb-3">
                        <strong>Insurance</strong>
                        <b-row class="mt-1 small">
                            <b-col cols="4" v-if="parsedSidecar.PrimaryPlanName">
                                <strong>Plan: </strong>{{ parsedSidecar.PrimaryPlanName }}
                            </b-col>
                            <b-col cols="4" v-if="parsedSidecar.PrimaryBIN">
                                <strong>BIN: </strong>{{ parsedSidecar.PrimaryBIN }}
                            </b-col>
                            <b-col cols="4" v-if="parsedSidecar.PrimaryPCN">
                                <strong>PCN: </strong>{{ parsedSidecar.PrimaryPCN }}
                            </b-col>
                        </b-row>
                        <b-row class="small">
                            <b-col cols="4" v-if="parsedSidecar.PrimaryGroupNumber">
                                <strong>Group: </strong>{{ parsedSidecar.PrimaryGroupNumber }}
                            </b-col>
                            <b-col cols="4" v-if="parsedSidecar.PrimaryCardHolderID">
                                <strong>Cardholder: </strong>{{ parsedSidecar.PrimaryCardHolderID }}
                            </b-col>
                            <b-col cols="4" v-if="parsedSidecar.PrimaryPlanPhone">
                                <strong>Phone: </strong>{{ parsedSidecar.PrimaryPlanPhone }}
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Adjudication -->
                    <div v-if="parsedSidecar.PrimaryFillAdjudicationCd" class="mb-3">
                        <strong>Adjudication</strong>
                        <b-row class="mt-1 small">
                            <b-col cols="3" v-if="parsedSidecar.PrimaryFillAdjudicationCd">
                                <strong>Primary Code: </strong>{{ parsedSidecar.PrimaryFillAdjudicationCd }}
                            </b-col>
                            <b-col cols="3" v-if="parsedSidecar.PrimaryFillAdjudicationDttm">
                                <strong>Primary At: </strong>{{ parsedSidecar.PrimaryFillAdjudicationDttm | formatDate('L LT') }}
                            </b-col>
                            <b-col cols="3" v-if="parsedSidecar.SecondaryFillAdjudicationCd">
                                <strong>Secondary Code: </strong>{{ parsedSidecar.SecondaryFillAdjudicationCd }}
                            </b-col>
                            <b-col cols="3" v-if="parsedSidecar.SecondaryFillAdjudicationDttm">
                                <strong>Secondary At: </strong>{{ parsedSidecar.SecondaryFillAdjudicationDttm | formatDate('L LT') }}
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Diagnosis Codes -->
                    <div v-if="sidecarDiagnosisCodes.length" class="mb-3">
                        <strong>Diagnosis Codes: </strong>
                        <span class="small">{{ sidecarDiagnosisCodes.join(', ') }}</span>
                    </div>

                    <!-- Additional Fields -->
                    <div v-if="sidecarAdditionalFields.length" class="mb-3">
                        <strong>Additional Fields</strong>
                        <b-table :items="sidecarAdditionalFields" :fields="['field', 'value']"
                            small striped class="mt-1 mb-0" />
                    </div>
                </div>
            </template>
            <hr>
            <!-- Outreach Tracking -->
            <b-row>
                <b-col>
                    <a href="#" @click.prevent="showOutreach = !showOutreach">
                        <i :class="{ 'fa fa-fw fa-chevron-right': true, 'fa-rotate-90': showOutreach }"
                            aria-hidden="true" style="transition: all .2s;" />
                        <strong>Outreach Attempts ({{ outreachHistory.length }})</strong>
                    </a>
                </b-col>
            </b-row>
            <div v-if="showOutreach" class="mt-2">
                <div v-if="outreachHistory.length" class="d-flex flex-column" style="gap: 8px;">
                    <b-card v-for="item in outreachHistory" :key="item.id" no-body class="p-2 card-plain">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <i :class="outreachTypeIcon(item.outreachType)" class="mr-1" />
                                <strong>{{ OutreachType[item.outreachType] }}</strong>
                                <span class="text-muted mx-1">&middot;</span>
                                {{ ContactedParty[item.contactedParty] }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 6px;">
                                <small class="text-muted">
                                    {{ formatOutreachDate(item.outreachDate) }}
                                    <span v-if="item.createdBy"> &middot; {{ item.createdBy }}</span>
                                    <span v-if="item.updatedBy"> &middot; edited by {{ item.updatedBy }}</span>
                                </small>
                                <div v-if="canEditOutreach || canDeleteOutreach" class="d-flex" style="gap: 4px;">
                                    <b-button v-if="canEditOutreach" size="sm" variant="outline-secondary"
                                        title="Edit Outreach" @click="startEditOutreach(item)">
                                        <i class="fas fa-pencil-alt" />
                                    </b-button>
                                    <b-button v-if="canDeleteOutreach" size="sm" variant="outline-danger"
                                        title="Delete Outreach" @click="deleteOutreach(item)">
                                        <i class="fas fa-trash" />
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        <div v-if="item.outreachNote" class="mt-1 text-muted" style="white-space: pre-wrap;">{{ item.outreachNote }}</div>
                    </b-card>
                </div>
                <p v-else class="text-muted ml-3">No outreach attempts recorded.</p>

                <b-button v-if="canEditPA" variant="primary" size="sm" class="mt-2 mb-2"
                    v-b-modal.log-outreach-modal>
                    <i class="fas fa-plus mr-1" /> Log Outreach
                </b-button>
            </div>
            <hr />
            <b-row>
                <b-col>
                    <NotesList ref="notes-list" :entity-id="value.id" :entity-type="EntityType.PriorAuthorization"
                        :paging="false" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <NotesForm :entity-id="value.id" :entity-type="EntityType.PriorAuthorization"
                        :templates="noteTemplates" @saved="refreshNotes" />
                </b-col>
            </b-row>
            <hr>
            <CoverMyMedsResponseComponent ref="cmmResponseSection" v-model="cmmResponse" :displayValue="displayValue" />
        </b-container>

        <template #modal-footer>
            <div class="w-100 d-flex">
                <div class="d-flex flex-fill" style="gap: 6px;">
                    <b-button v-b-modal.patient_documents_modal variant="secondary" size="sm">
                        Patient Docs...
                    </b-button>
                    <b-button variant="info" size="sm" v-b-modal.cmm-id-add v-if="canEditPA">
                        {{ value.coverMyMedsId ? "Update CMM Id" : "Add CMM Id" }}
                    </b-button>
                    <b-button variant="success" @click="createPA" v-if="!isNonScript && !value.coverMyMedsId && canCreatePA" size="sm"
                        :disabled="isCreatingPA">
                        Start PA
                    </b-button>
                    <b-button variant="success" @click="submitNonScriptToCMM" v-if="isNonScript && !value.coverMyMedsId && canCreatePA" size="sm"
                        :disabled="isSubmittingNonScriptPA">
                        <b-spinner v-if="isSubmittingNonScriptPA" small class="mr-1" />
                        Start PA (CMM)
                    </b-button>
                    <b-dropdown v-if="faxTemplateOptions.length || prescriberContactTemplateOptions.length"
                        text="Fax" variant="secondary" size="sm" dropup>
                        <b-dropdown-item v-if="faxTemplateOptions.length" v-b-modal.prior-auth-fax>
                            PA Fax...
                        </b-dropdown-item>
                        <b-dropdown-item v-if="prescriberContactTemplateOptions.length" v-b-modal.prescriber-contact-fax>
                            Prescriber Fax...
                        </b-dropdown-item>
                    </b-dropdown>
                    <b-dropdown v-if="canCreatePA" text="Workflows" variant="secondary" size="sm" dropup>
                        <b-dropdown-item v-if="!isNonScript" @click="openDevelopHealthModal">
                            Start Develop Health
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="(!isNonScript && value.prescription?.packageID) || (isNonScript && (value.drugNDC || parsedSidecar?.NDC))"
                            @click="startSurescriptsEpa" :disabled="isStartingSurescriptsEpa">
                            <b-spinner v-if="isStartingSurescriptsEpa" small class="mr-1" />
                            Start ePA (SureScripts)
                        </b-dropdown-item>
                    </b-dropdown>
                    <b-button v-if="isDenied && canEditPA" variant="warning" size="sm"
                        @click="showAppealTaskModal = true">
                        Create Appeal PA
                    </b-button>
                </div>
                <div class="d-flex" style="gap: 6px;">
                    <b-button variant="primary" size="sm" @click="savePriorAuthorization" :disabled="lockedByOtherUser">
                        <strong>Save</strong>
                    </b-button>
                </div>
            </div>
        </template>

        <patient-notes-modal-component v-if="value?.patient?.id" :entity-id="value.patient.id" />
        <doctor-notes-modal-component v-if="value?.prescriber?.id" :entity-id="value.prescriber.id" />
        <drug-notes-modal-component v-if="value?.prescription?.packageID" :entity-id="value.prescription.packageID"
            :drug-source="value.prescription.drugSource" />

        <b-modal id="log-outreach-modal" :title="editingOutreachId ? 'Edit Outreach' : 'Log Outreach'" no-fade
            :ok-title="editingOutreachId ? 'Save Outreach' : 'Log Outreach'"
            :ok-disabled="isSavingOutreach" @ok.prevent="saveOutreach" @hidden="resetOutreachForm">
            <b-row>
                <b-col cols="6">
                    <b-form-group label="Type">
                        <b-form-select v-model="newOutreach.outreachType" :options="outreachTypeOptions" />
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group label="Contacted Party">
                        <b-form-select v-model="newOutreach.contactedParty" :options="contactedPartyOptions" />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-form-group label="Date">
                <el-date-picker v-model="newOutreach.outreachDate" format="MM/dd/yyyy" type="date"
                    style="width: 100%;" />
            </b-form-group>
            <b-form-group label="Note">
                <b-form-textarea v-model="newOutreach.outreachNote" class="textarea-autosize"
                    placeholder="Optional note..." />
            </b-form-group>
        </b-modal>

        <b-modal id="cmm-id-add" :title="value.coverMyMedsId ? 'Update CMM Id' : 'Add CMM Id'" @ok="cmmOk" no-fade>
            <b-row>
                <b-col>
                    <b-input v-model="value.coverMyMedsId" />
                </b-col>
            </b-row>
        </b-modal>
        <b-modal id="prior-auth-fax" title="Prior Auth Fax" no-fade
            :size="showAttachPatientDoc ? 'lg' : null" ok-title="Send Fax" scrollable
            :ok-disabled="!selectedFaxTemplate || !isFaxNumberValid" @ok="sendFax" @hidden="resetFaxModal"
            @show="onPriorAuthFaxShow">
            <b-form-group label="Template *">
                <b-select v-model="selectedFaxTemplate" :options="faxTemplateOptions" />
            </b-form-group>
            <b-form-group label="Fax Number *">
                <b-select v-model="faxNumberSource" :options="faxNumberSourceOptions"
                    @change="onFaxNumberSourceChange" />
                <b-input v-if="faxNumberSource === 'custom'" v-model="faxNumber" class="mt-1"
                    placeholder="Enter fax number..." autofocus
                    :state="faxNumber ? isFaxNumberValid : null" />
            </b-form-group>
            <b-form-group label="Notes to Include">
                <b-textarea v-model="faxNotesToInclude" />
            </b-form-group>

            <a href="#" @click.prevent="showAttachPatientDoc = !showAttachPatientDoc">
                <i :class="{ 'fa fa-fw fa-chevron-right': true, 'fa-rotate-90': showAttachPatientDoc }"
                    aria-hidden="true" style="transition: all .2s;" />
                Attach Patient Documents...
            </a>

            <PatientDocumentsForm v-show="showAttachPatientDoc" list-only selectable :patient="value?.patient"
                @selectedImageIdsUpdate="selectedFaxAttachments" />

            <b-button class="mt-2" variant="outline-primary" size="sm" :disabled="!selectedFaxTemplate"
                @click="previewPriorAuthFax">
                Preview
            </b-button>

            <template v-if="isSendingFax" #modal-footer>
                <div class="text-center w-100">
                    Sending Fax...
                    <b-spinner small />
                </div>
            </template>
        </b-modal>

        <b-modal id="prescriber-contact-fax" title="Prescriber Contact Fax" no-fade
            ok-title="Send Fax" scrollable
            :ok-disabled="!selectedPrescriberContactTemplate || !isPrescriberContactFaxNumberValid" @ok="sendPrescriberContactFax"
            @hidden="resetPrescriberContactFaxModal" @show="onPrescriberContactFaxShow">
            <b-form-group label="Template *">
                <b-select v-model="selectedPrescriberContactTemplate" :options="prescriberContactTemplateOptions" />
            </b-form-group>
            <b-form-group label="Fax Number *">
                <b-select v-model="prescriberContactFaxSource" :options="faxNumberSourceOptions"
                    @change="onPrescriberContactFaxSourceChange" />
                <b-input v-if="prescriberContactFaxSource === 'custom'" v-model="prescriberContactFaxNumber" class="mt-1"
                    placeholder="Enter fax number..." autofocus
                    :state="prescriberContactFaxNumber ? isPrescriberContactFaxNumberValid : null" />
            </b-form-group>
            <b-form-group label="Notes to Include">
                <b-textarea v-model="prescriberContactFaxNotes" />
            </b-form-group>

            <b-button class="mt-2" variant="outline-primary" size="sm" :disabled="!selectedPrescriberContactTemplate"
                @click="previewPrescriberContactFax">
                Preview
            </b-button>

            <template v-if="isSendingPrescriberContactFax" #modal-footer>
                <div class="text-center w-100">
                    Sending Fax...
                    <b-spinner small />
                </div>
            </template>
        </b-modal>

        <FaxPreviewModal ref="previewModal" modal-id="pa-fax-preview" />

        <b-modal v-model="showDevelopHealthModal" title="Start Develop Health PA" size="lg" scrollable
            :ok-disabled="isSubmittingDevelopHealth"
            ok-title="Send to Develop Health" @ok="submitToDevelopHealth" @hidden="resetDevelopHealthModal">
            <p>Select documents to send to Develop Health. Insurance and prescription data will be pulled automatically from the patient's plan and fill information.</p>

            <b-table :items="developHealthPatientDocs" :fields="developHealthDocFields" small striped>
                <template #cell(select)="row">
                    <b-form-checkbox v-model="row.item.selected" @change="onDocSelectionChange(row.item)" />
                </template>
                <template #cell(fileName)="row">
                    <strong>{{ row.value }}</strong>
                </template>
                <template #cell(docType)="row">
                    <b-form-select v-if="row.item.selected" v-model="row.item.docType"
                        :options="developHealthDocTypeOptions" size="sm" />
                    <span v-else class="text-muted">-</span>
                </template>
                <template #cell(created)="row">
                    {{ row.value | formatDate('L LT') }}
                </template>
            </b-table>

            <template v-if="isSubmittingDevelopHealth" #modal-footer>
                <div class="text-center w-100">
                    Submitting to Develop Health...
                    <b-spinner small />
                </div>
            </template>
        </b-modal>

        <b-modal v-model="showAppealTaskModal" title="Create Appeal PA" size="md"
            ok-title="Create Appeal PA" :ok-disabled="isCreatingAppealTask"
            @ok="createAppealTask" @hidden="resetAppealTaskModal">
            <b-form-group label="Description (optional)">
                <b-textarea v-model="appealTaskDescription" rows="3" size="sm"
                    placeholder="Reason for appeal (added as a note on the original PA)" />
            </b-form-group>
            <template v-if="isCreatingAppealTask" #modal-footer>
                <div class="text-center w-100">
                    Creating appeal PA...
                    <b-spinner small />
                </div>
            </template>
        </b-modal>
    </b-modal>
</template>

<script lang="ts">

import Vue from "vue";
import { Component, Prop, Mixins, Ref, Watch } from "vue-property-decorator";
import { LockHandler } from "@/mixins/LockHandler";

import moment from "moment";
import Axios, { AxiosError } from 'axios';

import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import TextUtils from "@/util/TextUtils";
import { enumToOptions } from "@/util/EnumOptions";

import { FaxForm, Prescription, RefillStatusCode } from "@/models/Prescription";
import { Prescriber } from "@/models/Prescriber";
import { Claim } from "@/models/Claim";
import { PatientDocument } from "@/models/PatientDocument";
import { PatientPlan } from "@/models/PatientPlan";
import {
    PriorAuthorization,
    PriorAuthorizationStatusCode,
    CoverMyMedsResponse,
    PriorAuthStatusReason,
    PriorAuthorizationType,
    PriorAuthorizationOutreach,
    OutreachType,
    ContactedParty,
} from "@/models/PriorAuthorization";

import Card from "@/components/Cards/Card.vue";
import CoverMyMedsResponseComponent from "@/components/PriorAuthorization/CoverMyMedsResponseComponent.vue";
import PatientDocumentsModal from '@/components/Patient/PatientDocumentsModal.vue';
import { DatePicker } from "element-ui";
import EnumSelector from "../EnumSelector.vue";
import SearchComponent from "../SearchComponent.vue";
import NotesList from "../Note/NotesList.vue";
import NotesForm from "../Note/NotesForm.vue";
import { EntityType } from "@/models/EntityType";
import { PriorAuthFaxTemplate, PrescriberContactTemplate } from "@/models/FaxTemplates";
import { BvModalEvent } from "bootstrap-vue";
import DoctorNotesModalComponent from "@/components/Note/DoctorNotesModalComponent.vue";
import PatientNotesModalComponent from "@/components/Note/PatientNotesModalComponent.vue";
import DrugNotesModalComponent from "@/components/Note/DrugNotesModalComponent.vue";
import { Permission } from "@/models/Permission";
import { Program } from "@/models/Program";
import { Drug, DrugStatus } from "@/models/Drug/Drug";
import PatientDocumentsForm from "../Patient/PatientDocumentsForm.vue";
import FaxPreviewModal from "@/components/Fax/FaxPreviewModal.vue";

@Component({
    name: "PriorAuthorizationDetails",
    components: {
        DrugNotesModalComponent,
        PatientNotesModalComponent,
        DoctorNotesModalComponent,
        Card,
        CoverMyMedsResponseComponent,
        PatientDocumentsModal,
        [DatePicker.name]: DatePicker,
        EnumSelector,
        SearchComponent,
        NotesList,
        NotesForm,
        PatientDocumentsForm,
        FaxPreviewModal
    },
})
export default class PriorAuthorizationDetails extends Mixins(LockHandler) {
    @Ref('notes-list') protected notesList!: NotesList;
    @Ref('previewModal') private previewModal!: FaxPreviewModal;
    @Prop() protected value!: PriorAuthorization;
    @Prop() private id!: string;

    private cmmResponse: CoverMyMedsResponse = {} as CoverMyMedsResponse;
    private noteTemplates: string[] = [];

    private selectedPatientPlan = new PatientPlan();
    private completeReasons: PriorAuthStatusReason[] = [];
    private customReason = false;
    private lastDocumentDate: Date | null = null;
    private isCreatingPA = false;
    private lockedByOtherUser = false;

    private showRxDetails = false;
    private fullPrescription: Prescription | null = null;
    private rxDetailsError = false;

    protected PriorAuthorizationType = PriorAuthorizationType;
    protected PriorAuthorizationStatusCode = PriorAuthorizationStatusCode;
    protected EntityType = EntityType;

    private faxTemplates: PriorAuthFaxTemplate[] = [];
    private selectedFaxTemplate: PriorAuthFaxTemplate | null = null;
    protected faxNotesToInclude = "";

    protected faxNumberSource = 'prescriber';
    protected faxNumber = '';
    protected showAttachPatientDoc = false;
    protected selectedPatientDocuments: number[] = [];

    // Prescriber contact fax state
    private prescriberContactTemplates: PrescriberContactTemplate[] = [];
    private selectedPrescriberContactTemplate: PrescriberContactTemplate | null = null;
    protected prescriberContactFaxNotes = "";
    protected prescriberContactFaxNumber = '';
    protected prescriberContactFaxSource = 'prescriber';
    protected isSendingPrescriberContactFax = false;

    // Develop Health state
    protected showDevelopHealthModal = false;
    protected isSubmittingDevelopHealth = false;
    protected developHealthPatientDocs: Array<{
        imageId: number;
        fileName: string;
        created: Date;
        selected: boolean;
        docType: string;
    }> = [];

    protected developHealthDocFields = [
        { key: 'select', label: '' },
        { key: 'fileName', label: 'File Name' },
        { key: 'docType', label: 'Document Type' },
        { key: 'created', label: 'Uploaded' }
    ];

    protected developHealthDocTypeOptions = [
        { value: '', text: 'Select type...' },
        { value: 'insurance', text: 'Insurance Card' },
        { value: 'evidence', text: 'Evidence' }
    ];

    // Non-script PA linking
    protected selectedPrescriber: Prescriber = new Prescriber();
    protected selectedDrug: Drug = new Drug();
    protected selectedProgram: Program = new Program();
    protected programExplicitlyChanged = false;

    // Non-script PA CMM submission
    protected isSubmittingNonScriptPA = false;

    // SureScripts ePA
    protected isStartingSurescriptsEpa = false;

    // Sidecar data display
    protected showSidecar = false;
    // Populated only by lookupDispensingDirectory, hence the name: the dispensing numbers the
    // template renders come from the computeds below, which fall back to the CSV sidecar.
    protected directoryFax: string | null = null;
    protected directoryPhone: string | null = null;
    protected directoryHours: string | null = null;
    protected isLoadingDispensing = false;

    // Outreach tracking
    protected OutreachType = OutreachType;
    protected ContactedParty = ContactedParty;
    protected showOutreach = false;
    protected outreachHistory: PriorAuthorizationOutreach[] = [];
    protected isSavingOutreach = false;
    // Null while logging a new entry; holds the entry's id while the shared modal is editing one.
    protected editingOutreachId: number | null = null;
    protected newOutreach: Partial<PriorAuthorizationOutreach> = {
        outreachType: OutreachType.Phone,
        contactedParty: ContactedParty.Prescriber,
        outreachDate: new Date(),
        outreachNote: '',
    };

    protected outreachTypeOptions = enumToOptions(OutreachType);
    protected contactedPartyOptions = enumToOptions(ContactedParty);

    // Appeal PA creation
    protected showAppealTaskModal = false;
    protected isCreatingAppealTask = false;
    protected appealTaskDescription = '';

    async getPriorAuthConfigs() {
        try {
            const resp = await Axios.get<any>("/PriorAuthorization/Configs");
            this.completeReasons = resp.data.reasons;
            this.noteTemplates = resp.data.noteTemplates;
            this.faxTemplates = resp.data.faxTemplates;
            this.prescriberContactTemplates = resp.data.prescriberContactTemplates || [];

        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    get canEnterCustomReason(): boolean {
        const hasPermission = this.$permissions.some((p: Permission) => p.permissionCodeName.toLowerCase() === "services/electronic pa custom reasons");
        return hasPermission;
    }

    get canCreatePA(): boolean {
        if (this.lockedByOtherUser) return false;
        return this.$permissions.some((p: Permission) =>
            p.permissionCodeName.toLowerCase() === "services/prior authorization" ||
            p.permissionCodeName.toLowerCase() === "patient dashboard/create pa"
        );
    }

    get canEditPA(): boolean {
        if (this.lockedByOtherUser) return false;
        return this.$permissions.some((p: Permission) =>
            p.permissionCodeName.toLowerCase() === "services/prior authorization"
        );
    }

    // Outreach edit and delete are assignable independently of each other and of Services/Prior
    // Authorization, so neither is implied by general PA access.
    get canEditOutreach(): boolean {
        if (this.lockedByOtherUser) return false;
        return this.$permissions.some((p: Permission) =>
            p.permissionCodeName.toLowerCase() === "services/edit prior authorization outreach"
        );
    }

    get canDeleteOutreach(): boolean {
        if (this.lockedByOtherUser) return false;
        return this.$permissions.some((p: Permission) =>
            p.permissionCodeName.toLowerCase() === "services/delete prior authorization outreach"
        );
    }

    get isNonScript(): boolean {
        return this.value?.isNonScript ?? false;
    }

    get isDenied(): boolean {
        return this.value?.status === PriorAuthorizationStatusCode.Denied;
    }

    get parsedSidecar(): any | null {
        if (!this.value?.sidecarJson) return null;
        try { return JSON.parse(this.value.sidecarJson); }
        catch { return null; }
    }

    get sidecarRejectInfo(): Array<{ code: string; message: string }> {
        const s = this.parsedSidecar;
        if (!s) return [];
        const items: Array<{ code: string; message: string }> = [];
        const codes = [s.PrimaryRejectCd01, s.PrimaryRejectCd02, s.PrimaryRejectCd03, s.PrimaryRejectCd04, s.PrimaryRejectCd05];
        const msgs = [s.PrimaryRejectMsg1, s.PrimaryRejectMsg2, s.PrimaryRejectMsg3, s.PrimaryRejectMsg4, s.PrimaryRejectMsg5];

        codes.forEach((code, i) => {
            if (code) items.push({ code, message: msgs[i] || '' });
        });

        // Secondary reject codes
        if (s.SecondaryRejectCd01) items.push({ code: s.SecondaryRejectCd01, message: s.SecondaryRejectMsg1 || '' });
        if (s.SecondaryRejectCd02) items.push({ code: s.SecondaryRejectCd02, message: s.SecondaryRejectMsg2 || '' });
        if (s.SecondaryRejectCd03) items.push({ code: s.SecondaryRejectCd03, message: s.SecondaryRejectMsg3 || '' });
        if (s.SecondaryRejectCd04) items.push({ code: s.SecondaryRejectCd04, message: s.SecondaryRejectMsg4 || '' });
        if (s.SecondaryRejectCd05) items.push({ code: s.SecondaryRejectCd05, message: s.SecondaryRejectMsg5 || '' });

        return items;
    }

    get sidecarDiagnosisCodes(): string[] {
        const s = this.parsedSidecar;
        if (!s) return [];
        return [s.PrimaryDiagnosisCd, s.PrimaryDiagnosisCd2, s.PrimaryDiagnosisCd3,
            s.PrimaryDiagnosisCd4, s.PrimaryDiagnosisCd5, s.SecondaryDiagnosisCd]
            .filter((c: string | null) => !!c);
    }

    get sidecarAdditionalFields(): Array<{ field: string; value: string }> {
        const af = this.parsedSidecar?.AdditionalFields;
        if (!af || typeof af !== 'object' || Array.isArray(af)) return [];
        return Object.entries(af)
            .filter(([, v]) => v)
            .map(([k, v]) => ({ field: k, value: v as string }))
            .sort((a, b) => a.field.localeCompare(b.field));
    }

    get drugDisplayName(): string {
        if (this.value?.prescription?.productNameShort) return this.value.prescription.productNameShort;
        if (this.value?.drugName) return this.value.drugName;
        // Fall back to sidecar data
        return this.parsedSidecar?.ProductName || '';
    }

    get prescriberDisplayName(): string {
        if (this.value?.prescriber) {
            return `${this.value.prescriber.lastName}, ${this.value.prescriber.firstName}`;
        }
        // Fall back to sidecar data
        const s = this.parsedSidecar;
        if (s?.PrescriberLastName) return `${s.PrescriberLastName}, ${s.PrescriberFirstName || ''}`;
        return '';
    }

    get hasFaxTemplates() {
        return !!this.faxTemplates.length;
    }

    get faxTemplateOptions() {
        const programId = this.value.prescription?.programID || this.value.programId;
        return this.faxTemplates.filter(ft => {
            if (!ft.programs?.length) return true;
            return ft.programs.some(p => p.id === programId);
        }).map(ft => ({ value: ft, text: ft.name }));
    }

    get prescriberContactTemplateOptions() {
        return this.prescriberContactTemplates.map(t => ({ value: t, text: t.name }));
    }

    get prescriberFaxNumber(): string | null {
        // Use linked prescriber fax number first, then fall back to sidecar data
        if (this.value?.prescriber?.faxNumber) return this.value.prescriber.faxNumber;
        if (this.parsedSidecar?.PrescriberFax) return this.parsedSidecar.PrescriberFax;
        return null;
    }

    get dispensingPhoneNumber(): string | null {
        // SureScripts directory value first, then the phone the partner's CSV supplied
        return this.directoryPhone || this.parsedSidecar?.OriginPharmacyPhone || null;
    }

    get dispensingFaxNumber(): string | null {
        // SureScripts directory value first, then the fax the partner's CSV supplied
        return this.directoryFax || this.parsedSidecar?.OriginPharmacyFax || null;
    }

    get faxNumberSourceOptions() {
        const options: Array<{ value: string; text: string }> = [];
        if (this.prescriberFaxNumber && this.isValidFaxNumber(this.prescriberFaxNumber)) {
            options.push({ value: 'prescriber', text: `Prescriber Fax (${this.prescriberFaxNumber})` });
        }
        if (this.value?.prescriber?.altFaxNumber && this.isValidFaxNumber(this.value.prescriber.altFaxNumber)) {
            options.push({ value: 'alt', text: `Alternate Prescriber Fax (${this.value.prescriber.altFaxNumber})` });
        }
        if (this.dispensingFaxNumber && this.isValidFaxNumber(this.dispensingFaxNumber)) {
            options.push({ value: 'dispensing', text: `Dispensing Fax (${TextUtils.formatFaxNumber(this.dispensingFaxNumber)})` });
        }
        options.push({ value: 'custom', text: 'Custom' });
        return options;
    }

    get isFaxNumberValid(): boolean {
        return this.isValidFaxNumber(this.faxNumber);
    }

    get isPrescriberContactFaxNumberValid(): boolean {
        return this.isValidFaxNumber(this.prescriberContactFaxNumber);
    }

    private isValidFaxNumber(number: string): boolean {
        if (!number) return false;
        const digits = number.replace(/\D/g, '');
        return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
    }

    get faxRecipientDisplay(): string {
        if (this.value?.prescriber) {
            return `${this.value.prescriber.lastName}, ${this.value.prescriber.firstName} (${this.prescriberFaxNumber})`;
        }
        const s = this.parsedSidecar;
        if (s?.PrescriberLastName) {
            return `${s.PrescriberLastName}, ${s.PrescriberFirstName || ''} (${s.PrescriberFax})`;
        }
        return this.prescriberFaxNumber || '';
    }

    mounted() {
        this.getPriorAuthConfigs();
        this.selectedPatientPlan = Object.assign(new PatientPlan(), this.value.patientPlan);
    }

    get completeReasonOptions() {
        return this.completeReasons
            .filter(r => !r.status || r.status === this.value.status)
            .map(r => (r.name));
    }

    completeChosen(item: any) {
        this.value.completeReason = item;
    }

    onShow() {
        this.resetModal();
        this.acquirePriorAuthLock();

        this.$nextTick(() => {
            this.loadPatientDocuments();
            this.loadOutreach();
            if (this.isNonScript) {
                this.selectedPrescriber = new Prescriber(this.value?.prescriber || undefined);
                if (this.value?.drugName || this.value?.drugNDC) {
                    this.selectedDrug = Object.assign(new Drug(), {
                        productNameShort: this.value.drugName,
                        ndc: this.value.drugNDC,
                        activeStatus: DrugStatus.DrugStatusEnum.Active,
                    });
                }
                this.lookupDispensingDirectory();
            }
            // Initialize program from existing data
            if (this.value?.programId) {
                this.selectedProgram = Object.assign(new Program(), {
                    id: this.value.programId,
                    name: this.value.programName
                });
            } else {
                this.selectedProgram = new Program();
            }
        });
    }

    async acquirePriorAuthLock() {
        const paId = this.value?.id;
        if (!paId) {
            await this.clearAllLocks();
            return;
        }

        try {
            await this.setExclusiveLock(`/PriorAuthorization/${paId}/lock`, 60000);
            this.lockedByOtherUser = false;
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.lockedByOtherUser = true;
                this.showLockedMessage(
                    EntityType.PriorAuthorization,
                    paId.toString(),
                    paId.toString(),
                    error.response.data,
                );
            } else {
                this.$notification(NotificationOptions.error(err));
            }
        }
    }

    get status() {
        if (!this.value) return "";
        return PriorAuthorizationStatusCode[this.value.status];
    }

    get createDate() {
        if (!this.value) return "";
        return moment.utc(this.value.created).format("L");
    }

    get updateDate() {
        if (!this.value) return "";
        return moment.utc(this.value.updated).format("L");
    }

    get noteFields(): any[] {
        return [
            { key: "created", label: "", formatter: (val: any) => moment.utc(val).local().format("L LT") },
        ];
    }

    get patientName() {
        if (!this.value?.patient) return '';
        return this.value.patient.displayNamesForPerson();
    }

    get patientDateOfBirth() {
        if (!this.value?.patient?.dateOfBirth) return "";
        return moment.utc(this.value.patient.dateOfBirth).format("L");
    }

    get hasSelectedPlan(): boolean {
        return !!this.selectedPatientPlan?.id;
    }

    get requiresReason() {
        const reasonStatuses = [
            PriorAuthorizationStatusCode.Abandoned,
            PriorAuthorizationStatusCode.Denied,
            PriorAuthorizationStatusCode.Cancelled,
        ];

        return reasonStatuses.includes(this.value.status);
    }

    async lookupDispensingDirectory() {
        const npi = this.parsedSidecar?.OriginPharmacyNPI;
        if (!npi) return;
        this.isLoadingDispensing = true;
        try {
            const resp = await Axios.get("/StoredPharmacy/directory-search", { params: { npi } });
            const org = resp.data?.[0]?.organization;
            const fax = org?.communicationNumbers?.fax?.number;
            const phone = org?.communicationNumbers?.primaryTelephone?.number;
            const hours = org?.pharmacyHoursOfOperation;
            if (fax) this.directoryFax = fax;
            if (phone) this.directoryPhone = phone;
            if (hours) this.directoryHours = hours;
        } catch {
            // Silent fail - directory info display is optional
        } finally {
            this.isLoadingDispensing = false;
        }
    }

    async onModalHidden() {
        // Reap every lock this modal acquired — registered or still in flight — and only
        // then signal 'closed'. Awaiting the release before emitting lets a host refresh its
        // queue on 'closed' and read post-release lock state; refreshing on the modal's earlier
        // 'hide' would race the release and re-render a stale "locked" padlock for this PA.
        await this.clearAllLocks();
        this.resetModal();
        this.$emit('closed');
    }

    resetModal() {
        this.cmmResponse = {} as CoverMyMedsResponse;
        this.customReason = false;
        this.programExplicitlyChanged = false;
        // Clear the previous PA's program so the picker never opens hydrating it for this one.
        this.selectedProgram = new Program();
        this.showSidecar = false;
        this.showOutreach = false;
        this.outreachHistory = [];
        this.selectedDrug = new Drug();
        this.selectedPrescriber = new Prescriber();
        this.directoryFax = null;
        this.directoryPhone = null;
        this.directoryHours = null;
        this.isLoadingDispensing = false;
        this.showRxDetails = false;
        this.fullPrescription = null;
        this.rxDetailsError = false;
    }

    onProgramInput(program: Program | null) {
        // The picker also emits when it hydrates the bound program by id, so only a program that
        // differs from the one stored on this PA counts as the user changing it.
        this.programExplicitlyChanged = (program?.id || 0) !== (this.value?.programId || 0);
    }

    async savePriorAuthorization(closeModal: boolean = true, throwOnError: boolean = false) {
        try {
            if (this.requiresReason && !this.value.completeReason?.trim()) {
                this.$notification(NotificationOptions.error("A reason is required."));
                return;
            }

            // Apply linked prescriber/drug/program for non-script PAs
            if (this.isNonScript) {
                if (this.selectedPrescriber?.id) {
                    this.value.prescriberId = this.selectedPrescriber.id;
                }
                if (this.selectedDrug?.drugId) {
                    this.value.drugName = this.selectedDrug.productNameShort;
                    this.value.drugNDC = this.selectedDrug.ndc;
                }
                // Apply program linking only if user explicitly changed it (0 signals SP to clear; null preserves existing)
                if (this.programExplicitlyChanged) {
                    this.value.programId = this.selectedProgram?.id || 0;
                }
            }

            const data = {
                ...this.value,
                patientPlan: this.selectedPatientPlan,
            };

            await Axios.post(`PriorAuthorization/Save`, data);

            this.$notification(NotificationOptions.successSaveNotificationPreset("Prior Authorization"));
            if (closeModal) this.close();
        } catch (err) {
            console.error('Error while saving PA', err);
            const error = err as AxiosError;
            if (error.response?.status === 418 && this.value?.id) {
                this.showLockedMessage(
                    EntityType.PriorAuthorization,
                    this.value.id.toString(),
                    this.value.id.toString(),
                    error.response.data,
                );
            } else {
                this.$notification(NotificationOptions.errorSaveNotificationPreset("Prior Authorization", error));
            }
            if (throwOnError) throw err;
        }
    }

    async createPA() {
        // Prevent duplicate PA creation
        if (this.isCreatingPA) {
            console.warn('PA creation already in progress');
            return;
        }

        const prescription = this.value.prescription;
        if (!prescription) return;

        this.isCreatingPA = true;

        try {
            const response = await Axios.post<PriorAuthorization>(`/PriorAuthorization/Create?paId=${this.value.id}`, prescription);

            if (response.data.responses.length) {
                const resp = response.data.responses[0];
                this.cmmResponse = resp;
                this.$notification(NotificationOptions.success("Prior Authorization initiated successfully"));
                this.$nextTick(() => {
                    const el = (this.$refs.cmmResponseSection as Vue)?.$el as HTMLElement;
                    if (el?.tagName) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                });
            } else {
                this.$notification(NotificationOptions.warning("No responses returned."));
            }
        } catch (err) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("PA", err as AxiosError));
            console.error('Error while creating PA', err);
        } finally {
            this.isCreatingPA = false;
        }
    }

    async submitNonScriptToCMM() {
        if (this.isSubmittingNonScriptPA || !this.value?.id) return;

        this.isSubmittingNonScriptPA = true;
        try {
            // Save first to ensure latest data is persisted (throw on error to prevent CMM submit if save fails)
            await this.savePriorAuthorization(false, true);

            const response = await Axios.post<PriorAuthorization>(
                `/PriorAuthorization/${this.value.id}/SubmitCoverMyMeds`
            );

            if (response.data.responses?.length) {
                this.cmmResponse = response.data.responses[0];
                this.$notification(NotificationOptions.success("PA submitted to CoverMyMeds successfully."));
            } else {
                this.$notification(NotificationOptions.warning("No responses returned from CoverMyMeds."));
            }
        } catch (err) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("CoverMyMeds PA", err as AxiosError));
            console.error('Error submitting non-script PA to CMM', err);
        } finally {
            this.isSubmittingNonScriptPA = false;
        }
    }

    async startSurescriptsEpa() {
        if (this.isStartingSurescriptsEpa) return;

        this.isStartingSurescriptsEpa = true;
        try {
            if (this.isNonScript) {
                // Save first to ensure latest sidecar data is persisted
                await this.savePriorAuthorization(false, true);
                // Non-script path -- uses PA ID
                await Axios.post(`/PriorAuthorization/${this.value.id}/SubmitSurescriptsEpa`);
            } else {
                // Script-tied path -- uses Prescription body (existing)
                await Axios.post(`/PriorAuthorization/Create/Surescripts`, this.value.prescription);
            }
            this.$notification(NotificationOptions.success("SureScripts ePA initiated successfully."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error('Error starting SureScripts ePA', err);
        } finally {
            this.isStartingSurescriptsEpa = false;
        }
    }

    get displayValue(): string {
        if (!this.value?.prescription) return this.drugDisplayName || '';
        const prescription = Object.assign(new Prescription(), this.value.prescription);

        return `${prescription.rxID} ${prescription.productNameShort} - ${this.formatDate(prescription.writtenDate)}`;
    }

    formatDate(d: Date): string {
        if (!d) return "";
        return moment(d).format("L");
    }

    completeFinish() {
        if (this.value.completeReason?.length) {
            this.savePriorAuthorization();
        } else {
            console.warn("No complete reason given, not saving.");
            this.$notification(NotificationOptions.warning("No complete reason given, not saving."));
        }
    }

    // button calls
    coverMyMedsClick() {
        const url = `https://covermymeds.com/request/view/${this.value.coverMyMedsId}`;
        window.open(url, "_blank");
    }

    cmmOk() {
        this.savePriorAuthorization(false);
        this.$bvModal.hide('cmm-id-add');
    }

    close() {
        this.$bvModal.hide(this.id);
    }

    async loadPatientDocuments() {
        if (!this.value?.patient?.id) return;

        try {
            const res = await Axios.get<Array<PatientDocument>>(`/patient/${this.value?.patient?.id}/get-documents/`);
            if (res.data?.length) this.calculateLastDocumentDate(res.data);
        } catch (err) {
            console.error("Error while loading patient documents.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    calculateLastDocumentDate(docs: Array<PatientDocument>) {
        if (!docs?.length) return;

        const res = docs.map(d => d.created).sort((d1: Date, d2: Date) => {
            return new Date(d2).getTime() - new Date(d1).getTime();
        })[0];

        this.lastDocumentDate = res;
    }

    get prescriptionDetailsRoute() {
        if (!this.value?.prescription) return {};
        return {
            name: 'PrescriptionDetails',
            params: {
                storeID: this.value.prescription.storeID,
                rxNumber: this.value.prescription.rxNumber,
                rfNumber: (this.value.prescription.rfNumber || 0),
            },
        };
    }

    get rxStatus(): string {
        if (!this.fullPrescription) return '';
        const fp = this.fullPrescription;
        const isExpired = !!fp.expirationDate && moment().isAfter(moment(fp.expirationDate), 'days');
        let status = isExpired ? 'Expired' : TextUtils.camelCaseToNormal(RefillStatusCode[fp.status]);
        if (fp.subStatusName) {
            status += ` (${fp.subStatusName})`;
        }
        return status;
    }

    get latestClaim(): Claim | null {
        if (!this.fullPrescription?.claims?.length) return null;
        return this.fullPrescription.claims[this.fullPrescription.claims.length - 1];
    }

    async toggleRxDetails() {
        this.showRxDetails = !this.showRxDetails;
        if (this.showRxDetails && !this.fullPrescription) {
            await this.loadFullPrescription();
        }
    }

    async loadFullPrescription() {
        const rx = this.value.prescription;
        if (!rx) return;

        this.rxDetailsError = false;
        try {
            const rxId = `${rx.storeID}-${rx.rxNumber}-${rx.rfNumber || 0}`;
            const [rxRes, claimsRes] = await Promise.all([
                Axios.get<Prescription>(`/api/Prescription/${rxId}`),
                Axios.get<Claim[]>(`/api/Insurance/GetClaims/${rx.storeID}/${rx.rxNumber}/${rx.rfNumber || 0}`)
            ]);
            this.fullPrescription = rxRes.data;
            this.fullPrescription.claims = claimsRes.data;
        } catch (err) {
            console.error("Error loading full prescription", err);
            this.$notification(NotificationOptions.error(err));
            this.rxDetailsError = true;
        }
    }

    // Driven by the Status control's input event, not a watcher on `value.status` — the modal stays
    // mounted while the host swaps the bound PA, so a watcher also fired on bind and wiped the saved reason.
    onStatusSelected(newStatus: PriorAuthorizationStatusCode) {
        if (newStatus === this.value.status) return;

        this.value.status = newStatus;
        this.value.completeReason = '';
        this.customReason = false;

        if (newStatus === PriorAuthorizationStatusCode.Approved) {
            if (!this.value.approvalDate) this.value.approvalDate = new Date();
            if (!this.value.expirationDate) this.value.expirationDate = moment().add(1, "year").toDate();
        }
    }

    @Watch("value")
    handleValueChange() {
        this.selectedPatientPlan = Object.assign(new PatientPlan(), this.value.patientPlan);
    }

    @Watch("value.id")
    onValueIdChange(newId: number | null | undefined) {
        if (!newId) return;
        this.acquirePriorAuthLock();
    }

    protected isSendingFax = false;

    // Builds the PA fax payload. Shared by send and preview so the preview reflects what would be sent.
    private buildPriorAuthFaxForm(): FaxForm {
        const faxForm = new FaxForm();

        // For script-tied PAs, include RxID; for non-script, leave it empty
        if (this.value?.prescription) {
            const script = Object.assign(new Prescription(), this.value.prescription);
            faxForm.rxID = script.rxID;
        }

        faxForm.fax = this.faxNumber;
        // The number alone does not say who it belongs to; the server needs the choice to log the
        // outreach against the party actually contacted.
        faxForm.faxNumberSource = this.faxNumberSource;
        faxForm.note = this.faxNotesToInclude;
        faxForm.imageIds = this.showAttachPatientDoc ? this.selectedPatientDocuments : [];

        // Set IDs for non-script PAs (backend needs these when no prescription)
        if (this.isNonScript) {
            faxForm.patientID = this.value?.patient?.id || 0;
            faxForm.prescriberID = this.value?.prescriberId || this.value?.prescriber?.id || 0;
            faxForm.programID = this.value?.programId || 0;
        }

        return faxForm;
    }

    // Renders the exact PA fax PDF without sending it or recording an outreach.
    previewPriorAuthFax() {
        if (!this.selectedFaxTemplate) return;
        const faxForm = this.buildPriorAuthFaxForm();
        this.previewModal.open(async () => {
            const res = await Axios.post(
                `PriorAuthorization/PreviewFax/${this.value.id}/${this.selectedFaxTemplate!.id}`,
                faxForm, { responseType: "blob" });
            return res.data;
        });
    }

    async sendFax(event: BvModalEvent) {
        event.preventDefault();

        try {
            const faxForm = this.buildPriorAuthFaxForm();

            this.isSendingFax = true;

            const res = await Axios.post<PatientDocument>(`PriorAuthorization/SendFax/${this.value.id}/${this.selectedFaxTemplate!.id}`, faxForm);

            if (!res.data.id) {
                this.$notification(NotificationOptions.warning("Fax was sent but no confirmation was returned."));
                return;
            }

            const msg = `${this.selectedFaxTemplate!.name} sent successfully and saved as a new patient document.`;

            this.$notification(
                NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success),
            );

            await this.loadOutreach();

            this.$bvModal.hide("prior-auth-fax");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isSendingFax = false;
        }
    }

    refreshNotes() {
        this.notesList.loadNotes();
    }

    selectedFaxAttachments(data: number[]) {
        this.selectedPatientDocuments = data;
    }

    onPriorAuthFaxShow() {
        const hasValidPrescriberFax = this.prescriberFaxNumber && this.isValidFaxNumber(this.prescriberFaxNumber);
        this.faxNumberSource = hasValidPrescriberFax ? 'prescriber' : 'custom';
        this.faxNumber = hasValidPrescriberFax ? this.prescriberFaxNumber! : '';
    }

    onFaxNumberSourceChange(source: string) {
        this.faxNumber = this.resolveFaxSource(source);
    }

    resetFaxModal() {
        this.faxNotesToInclude = '';
        this.faxNumber = '';
        this.faxNumberSource = 'prescriber';
        this.showAttachPatientDoc = false;
        this.selectedPatientDocuments = [];
        this.selectedFaxTemplate = null;
    }

    // Builds the prescriber-contact fax payload. Shared by send and preview.
    private buildPrescriberContactFaxForm(): FaxForm {
        const faxForm = new FaxForm();

        if (this.value?.prescription) {
            const script = Object.assign(new Prescription(), this.value.prescription);
            faxForm.rxID = script.rxID;
        }

        faxForm.fax = this.prescriberContactFaxNumber;
        faxForm.faxNumberSource = this.prescriberContactFaxSource;
        faxForm.note = this.prescriberContactFaxNotes;

        if (this.isNonScript) {
            faxForm.patientID = this.value?.patient?.id || 0;
            faxForm.prescriberID = this.value?.prescriberId || this.value?.prescriber?.id || 0;
            faxForm.programID = this.value?.programId || 0;
        }

        return faxForm;
    }

    // Renders the exact prescriber-contact fax PDF without sending it or recording an outreach.
    previewPrescriberContactFax() {
        if (!this.selectedPrescriberContactTemplate) return;
        const faxForm = this.buildPrescriberContactFaxForm();
        this.previewModal.open(async () => {
            const res = await Axios.post(
                `PriorAuthorization/PreviewPrescriberContactFax/${this.value.id}/${this.selectedPrescriberContactTemplate!.id}`,
                faxForm, { responseType: "blob" });
            return res.data;
        });
    }

    async sendPrescriberContactFax(event: BvModalEvent) {
        event.preventDefault();

        try {
            const faxForm = this.buildPrescriberContactFaxForm();

            this.isSendingPrescriberContactFax = true;

            const res = await Axios.post<PatientDocument>(
                `PriorAuthorization/SendPrescriberContactFax/${this.value.id}/${this.selectedPrescriberContactTemplate!.id}`,
                faxForm
            );

            if (!res.data.id) {
                this.$notification(NotificationOptions.warning("Fax was sent but no confirmation was returned."));
                return;
            }

            const msg = `${this.selectedPrescriberContactTemplate!.name} sent successfully and saved as a new patient document.`;
            this.$notification(
                NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success),
            );

            await this.loadOutreach();

            this.$bvModal.hide("prescriber-contact-fax");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isSendingPrescriberContactFax = false;
        }
    }

    onPrescriberContactFaxShow() {
        const hasValidPrescriberFax = this.prescriberFaxNumber && this.isValidFaxNumber(this.prescriberFaxNumber);
        this.prescriberContactFaxSource = hasValidPrescriberFax ? 'prescriber' : 'custom';
        this.prescriberContactFaxNumber = hasValidPrescriberFax ? this.prescriberFaxNumber! : '';
    }

    onPrescriberContactFaxSourceChange(source: string) {
        this.prescriberContactFaxNumber = this.resolveFaxSource(source);
    }

    resetPrescriberContactFaxModal() {
        this.prescriberContactFaxNotes = '';
        this.prescriberContactFaxNumber = '';
        this.selectedPrescriberContactTemplate = null;
        this.prescriberContactFaxSource = 'prescriber';
    }

    private resolveFaxSource(source: string): string {
        switch (source) {
            case 'prescriber': return this.prescriberFaxNumber || '';
            case 'alt': return this.value?.prescriber?.altFaxNumber || '';
            case 'dispensing': return TextUtils.formatFaxNumber(this.dispensingFaxNumber || '');
            case 'custom': return '';
            default: return '';
        }
    }


    // Develop Health methods
    async openDevelopHealthModal() {
        this.showDevelopHealthModal = true;
        await this.loadDevelopHealthDocs();
    }

    get developHealthDocSelections() {
        return this.developHealthPatientDocs.filter(d => d.selected);
    }

    onDocSelectionChange(item: any) {
        // Reset docType when deselected
        if (!item.selected) {
            item.docType = '';
        }
    }

    async loadDevelopHealthDocs() {
        if (!this.value?.patient?.id) return;

        try {
            const res = await Axios.get<Array<PatientDocument>>(`/patient/${this.value.patient.id}/get-documents/`);
            this.developHealthPatientDocs = (res.data || []).map(doc => ({
                imageId: doc.imageId,
                fileName: doc.fileName,
                created: doc.created,
                selected: false,
                docType: ''
            }));
        } catch (err) {
            console.error("Error loading patient documents for Develop Health", err);
        }
    }

    resetDevelopHealthModal() {
        this.developHealthPatientDocs = [];
        this.isSubmittingDevelopHealth = false;
    }

    async submitToDevelopHealth(event: BvModalEvent) {
        event.preventDefault();

        this.isSubmittingDevelopHealth = true;

        try {
            // Separate insurance and evidence documents
            const insuranceDocIds = this.developHealthDocSelections
                .filter(d => d.docType === 'insurance')
                .map(d => d.imageId);
            const evidenceDocIds = this.developHealthDocSelections
                .filter(d => d.docType === 'evidence')
                .map(d => d.imageId);

            const response = await Axios.post<{
                success: boolean;
                requestId?: string;
                portalUrl?: string;
                status?: string;
                message?: string;
            }>('/PriorAuthorization/DevelopHealth/Submit', {
                priorAuthorizationId: this.value.id,
                insuranceDocumentIds: insuranceDocIds,
                evidenceDocumentIds: evidenceDocIds
            });

            if (response.data.success) {
                let msg = response.data.message || "Successfully submitted to Develop Health.";
                if (response.data.portalUrl) {
                    msg += ` Portal URL: ${response.data.portalUrl}`;
                    // Open portal URL in new tab
                    window.open(response.data.portalUrl, "_blank");
                }
                this.$notification(NotificationOptions.success(msg));
                this.showDevelopHealthModal = false;
                this.refreshNotes();
            } else {
                this.$notification(NotificationOptions.error(response.data.message || "Submission failed."));
            }
        } catch (err) {
            console.error("Error submitting to Develop Health", err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isSubmittingDevelopHealth = false;
        }
    }

    // Outreach methods
    async loadOutreach() {
        if (!this.value?.id) return;
        try {
            const res = await Axios.get<PriorAuthorizationOutreach[]>(`/PriorAuthorization/${this.value.id}/outreach`);
            this.outreachHistory = res.data || [];
        } catch (err) {
            console.error("Error loading outreach", err);
        }
    }

    // The one modal both logs a new entry and saves an edit; editingOutreachId picks which.
    async saveOutreach() {
        if (!this.value?.id) return;
        const editingId = this.editingOutreachId;
        const url = `/PriorAuthorization/${this.value.id}/outreach`;
        const body = { priorAuthorizationId: this.value.id, ...this.newOutreach };
        this.isSavingOutreach = true;
        try {
            await (editingId ? Axios.put(`${url}/${editingId}`, body) : Axios.post(url, body));
            this.$notification(NotificationOptions.success(editingId ? "Outreach updated." : "Outreach logged."));
            await this.loadOutreach();
            this.$bvModal.hide('log-outreach-modal');
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isSavingOutreach = false;
        }
    }

    startEditOutreach(item: PriorAuthorizationOutreach) {
        this.editingOutreachId = item.id ?? null;
        this.newOutreach = {
            outreachType: item.outreachType,
            contactedParty: item.contactedParty,
            // The picker needs a Date; the API hands back a UTC string.
            outreachDate: moment.utc(item.outreachDate).local().toDate(),
            outreachNote: item.outreachNote ?? '',
        };
        this.$bvModal.show('log-outreach-modal');
    }

    async deleteOutreach(item: PriorAuthorizationOutreach) {
        if (!this.value?.id || !item.id) return;

        const confirmed = await this.$bvModal.msgBoxConfirm(
            "Delete this outreach entry? It will be removed from the outreach log and from outreach counts, including reports already run.",
            {
                title: "Delete Outreach",
                okTitle: "Delete",
                okVariant: "danger",
                cancelTitle: "Cancel",
                centered: true,
            });

        if (!confirmed) return;

        try {
            await Axios.delete(`/PriorAuthorization/${this.value.id}/outreach/${item.id}`);
            this.$notification(NotificationOptions.success("Outreach deleted."));
            await this.loadOutreach();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    resetOutreachForm() {
        this.editingOutreachId = null;
        this.newOutreach = {
            outreachType: OutreachType.Phone,
            contactedParty: ContactedParty.Prescriber,
            outreachDate: new Date(),
            outreachNote: '',
        };
    }

    formatOutreachDate(d: any): string {
        return moment.utc(d).local().format('l LT');
    }

    outreachTypeIcon(type: OutreachType): string {
        switch (type) {
            case OutreachType.Phone: return 'fas fa-phone';
            case OutreachType.Fax: return 'fas fa-fax';
            case OutreachType.Email: return 'fas fa-envelope';
            default: return 'fas fa-comment';
        }
    }

    // Appeal PA methods
    async createAppealTask(event: BvModalEvent) {
        event.preventDefault();
        if (!this.value?.id) return;

        this.isCreatingAppealTask = true;
        try {
            await Axios.post(`/PriorAuthorization/${this.value.id}/AppealTask`, {
                description: this.appealTaskDescription,
            });

            this.$notification(NotificationOptions.success("Appeal PA created."));
            this.showAppealTaskModal = false;
            this.refreshNotes();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isCreatingAppealTask = false;
        }
    }

    resetAppealTaskModal() {
        this.appealTaskDescription = '';
        this.isCreatingAppealTask = false;
    }

    @Watch('showAppealTaskModal')
    onShowAppealTaskModal(show: boolean) {
        if (!show) return;
        this.appealTaskDescription = `Appeal for Prior Authorization #${this.value.id} - ${this.drugDisplayName}`;
    }
}
</script>

<style scoped>
.note-body {
    word-break: break-word;
}
</style>
